import React, { useState, useEffect, useCallback } from 'react';
import {
  IconShield, IconPlus, IconTrash, IconDeviceFloppy,
  IconChevronRight, IconLock, IconUsers,
} from '@tabler/icons-react';
import api from '../api/api';
import { onBackdropMouseDown, onBackdropClick } from '../utils/modalDrag';

/* ──────────────────────────────────────────────
   권한 코드 → 카테고리 그룹핑 순서
────────────────────────────────────────────── */
const CATEGORY_ORDER = ['사용자 관리', '권한 관리', '시스템 관리'];

export default function RolePage({ me, showToast }) {
  const [roles, setRoles]           = useState([]);
  const [allPerms, setAllPerms]     = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [checkedPerms, setCheckedPerms] = useState(new Set());
  const [saving, setSaving]         = useState(false);
  const [dirty, setDirty]           = useState(false);
  const [loadError, setLoadError]   = useState(null);
  const [loading, setLoading]       = useState(true);

  /* 역할 추가 모달 */
  const [modalOpen, setModalOpen]   = useState(false);
  const [newCode, setNewCode]       = useState('');
  const [newName, setNewName]       = useState('');
  const [newDesc, setNewDesc]       = useState('');
  const [creating, setCreating]     = useState(false);

  const isMaster = me?.role === 'MASTER';

  /* ── API 조회 ── */
  const fetchRoles = useCallback(async () => {
    try {
      const res = await api.get('/roles');
      setRoles(res.data);
      return true;
    } catch (err) {
      const status = err.response?.status;
      const msg = status === 403
        ? '접근 권한이 없습니다. (ADMIN 또는 MASTER 계정 필요)'
        : `역할 목록 조회 실패 (${status ?? '네트워크 오류'})`;
      setLoadError(msg);
      showToast(msg, 'danger');
      return false;
    }
  }, [showToast]);

  const fetchPerms = useCallback(async () => {
    try {
      const res = await api.get('/roles/permissions');
      setAllPerms(res.data);
    } catch (err) {
      const status = err.response?.status;
      showToast(`권한 목록 조회 실패 (${status ?? '네트워크 오류'})`, 'danger');
    }
  }, [showToast]);

  useEffect(() => {
    setLoading(true);
    setLoadError(null);
    Promise.all([fetchRoles(), fetchPerms()]).finally(() => setLoading(false));
  }, []);

  /* ── 역할 선택 ── */
  const handleSelectRole = (role) => {
    if (dirty && !window.confirm('저장되지 않은 변경사항이 있습니다. 이동하시겠습니까?')) return;
    setSelectedRole(role);
    setCheckedPerms(new Set(role.permissions.map(p => p.id)));
    setDirty(false);
  };

  /* ── 체크박스 토글 ── */
  const togglePerm = (permId) => {
    if (!isMaster) return; // ADMIN은 읽기 전용
    setCheckedPerms(prev => {
      const next = new Set(prev);
      next.has(permId) ? next.delete(permId) : next.add(permId);
      return next;
    });
    setDirty(true);
  };

  /* ── 저장 ── */
  const handleSave = async () => {
    if (!selectedRole) return;
    setSaving(true);
    try {
      // 백엔드가 @RequestBody List<Long> 을 기대하므로 배열을 직접 전송
      await api.put(`/roles/${selectedRole.id}/permissions`, [...checkedPerms]);
      showToast('권한이 저장되었습니다.');
      setDirty(false);
      await fetchRoles();
      // 갱신된 역할 데이터로 selectedRole 업데이트
      setSelectedRole(prev => ({
        ...prev,
        permissions: allPerms.filter(p => checkedPerms.has(p.id)),
      }));
    } catch {
      showToast('권한 저장 실패', 'danger');
    } finally {
      setSaving(false);
    }
  };

  /* ── 역할 생성 ── */
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newCode.trim() || !newName.trim()) return;
    setCreating(true);
    try {
      await api.post('/roles', {
        code: newCode.trim().toUpperCase(),
        name: newName.trim(),
        description: newDesc.trim() || null,
      });
      showToast('역할이 생성되었습니다.');
      setModalOpen(false);
      setNewCode(''); setNewName(''); setNewDesc('');
      await fetchRoles();
    } catch (err) {
      showToast(err.response?.data?.message || '역할 생성 실패', 'danger');
    } finally {
      setCreating(false);
    }
  };

  /* ── 역할 삭제 ── */
  const handleDelete = async (role) => {
    if (role.isSystem) {
      showToast('시스템 역할은 삭제할 수 없습니다.', 'danger');
      return;
    }
    if (!window.confirm(`"${role.name}" 역할을 삭제하시겠습니까?`)) return;
    try {
      await api.delete(`/roles/${role.id}`);
      showToast('역할이 삭제되었습니다.');
      if (selectedRole?.id === role.id) {
        setSelectedRole(null);
        setCheckedPerms(new Set());
      }
      await fetchRoles();
    } catch (err) {
      showToast(err.response?.data?.message || '역할 삭제 실패', 'danger');
    }
  };

  /* ── 카테고리별 권한 그룹핑 ── */
  const grouped = CATEGORY_ORDER.reduce((acc, cat) => {
    const items = allPerms.filter(p => p.category === cat);
    if (items.length) acc[cat] = items;
    return acc;
  }, {});

  /* ── 역할 뱃지 색상 ── */
  const roleBadgeColor = (code) => {
    if (code === 'MASTER') return 'bg-purple';
    if (code === 'ADMIN')  return 'bg-primary';
    return 'bg-azure';
  };

  return (
    <div className="page-body">
      <div className="container-xl">

        {/* 상단 헤더 */}
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div>
            <div className="text-muted small">권한 관리</div>
            <h2 className="mb-0" style={{ fontSize: 20, fontWeight: 700 }}>역할 및 권한 설정</h2>
          </div>
          {isMaster && (
            <button className="btn btn-primary btn-sm d-flex align-items-center gap-1"
              onClick={() => setModalOpen(true)}>
              <IconPlus size={16} /> 역할 추가
            </button>
          )}
        </div>

        {/* 로딩 중 */}
        {loading && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" style={{ width: 40, height: 40 }} />
            <div className="text-muted mt-2">데이터 로딩 중...</div>
          </div>
        )}

        {/* 오류 */}
        {!loading && loadError && (
          <div className="alert alert-danger d-flex align-items-center gap-2">
            <IconShield size={20} />
            <div>
              <strong>조회 실패</strong><br />
              <small>{loadError}</small>
            </div>
          </div>
        )}

        {/* 본문 */}
        {!loading && !loadError && (
        <div className="row g-3">

          {/* ── 좌측: 역할 목록 ── */}
          <div className="col-md-4 col-lg-3">
            <div className="card h-100">
              <div className="card-header">
                <h4 className="card-title mb-0 d-flex align-items-center gap-2">
                  <IconShield size={18} /> 역할 목록
                </h4>
              </div>
              <div className="list-group list-group-flush">
                {roles.length === 0 && (
                  <div className="list-group-item text-muted text-center py-4">역할 없음</div>
                )}
                {roles.filter(r => r.code !== 'USER').map(role => {
                  const isActive = selectedRole?.id === role.id;
                  return (
                    <div
                      key={role.id}
                      className={`list-group-item list-group-item-action${isActive ? ' active' : ''}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSelectRole(role)}
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="flex-grow-1 overflow-hidden">
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <span className={`badge ${roleBadgeColor(role.code)} text-white`}>
                              {role.code}
                            </span>
                            {role.isSystem && (
                              <IconLock size={12} className={isActive ? 'text-white' : 'text-muted'} />
                            )}
                          </div>
                          <div className="fw-semibold text-truncate" style={{ fontSize: 14 }}>
                            {role.name}
                          </div>
                          <div className={`small ${isActive ? 'text-white opacity-75' : 'text-muted'}`}
                            style={{ fontSize: 11 }}>
                            <IconUsers size={11} className="me-1" />
                            {role.userCount ?? 0}명 · 권한 {role.permissions?.length ?? 0}개
                          </div>
                        </div>
                        <div className="d-flex align-items-center gap-1 ms-2">
                          {isMaster && !role.isSystem && (
                            <button
                              className={`btn btn-sm btn-icon ${isActive ? 'text-white' : 'text-danger'}`}
                              style={{ background: 'none', border: 'none', padding: '2px 4px' }}
                              onClick={(e) => { e.stopPropagation(); handleDelete(role); }}
                              title="삭제"
                            >
                              <IconTrash size={14} />
                            </button>
                          )}
                          <IconChevronRight size={14} className={isActive ? 'text-white' : 'text-muted'} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ── 우측: 권한 체크박스 ── */}
          <div className="col-md-8 col-lg-9">
            {!selectedRole ? (
              <div className="card h-100 d-flex align-items-center justify-content-center"
                style={{ minHeight: 300 }}>
                <div className="text-center text-muted py-5">
                  <IconShield size={48} className="mb-3 opacity-25" />
                  <div>좌측에서 역할을 선택하세요</div>
                </div>
              </div>
            ) : (
              <div className="card">
                <div className="card-header d-flex align-items-center justify-content-between">
                  <div>
                    <h4 className="card-title mb-0 d-flex align-items-center gap-2">
                      <span className={`badge ${roleBadgeColor(selectedRole.code)} text-white`}>
                        {selectedRole.code}
                      </span>
                      {selectedRole.name}
                    </h4>
                    {selectedRole.description && (
                      <div className="text-muted small mt-1">{selectedRole.description}</div>
                    )}
                  </div>
                  {isMaster && (
                    <button
                      className="btn btn-primary btn-sm d-flex align-items-center gap-1"
                      onClick={handleSave}
                      disabled={saving || !dirty}
                    >
                      {saving
                        ? <span className="spinner-border spinner-border-sm" />
                        : <IconDeviceFloppy size={16} />}
                      저장
                    </button>
                  )}
                </div>

                <div className="card-body">
                  {!isMaster && (
                    <div className="alert alert-info py-2 px-3 mb-3" style={{ fontSize: 13 }}>
                      <IconLock size={14} className="me-1" />
                      MASTER 계정만 권한을 수정할 수 있습니다.
                    </div>
                  )}

                  {Object.entries(grouped).map(([cat, perms]) => (
                    <div key={cat} className="mb-4">
                      <div className="text-uppercase text-muted fw-bold mb-2"
                        style={{ fontSize: 11, letterSpacing: '0.08em' }}>
                        {cat}
                      </div>
                      <div className="row g-2">
                        {perms.map(perm => {
                          const checked = checkedPerms.has(perm.id);
                          return (
                            <div key={perm.id} className="col-sm-6 col-lg-4">
                              <label
                                className={`d-flex align-items-start gap-2 p-3 rounded border
                                  ${isMaster ? 'cursor-pointer' : ''}
                                  ${checked
                                    ? 'border-primary bg-primary-lt'
                                    : 'border-secondary-subtle bg-light'}`}
                                style={{
                                  cursor: isMaster ? 'pointer' : 'default',
                                  transition: 'all 0.15s',
                                }}
                                onClick={(e) => { e.preventDefault(); togglePerm(perm.id); }}
                              >
                                <input
                                  type="checkbox"
                                  className="form-check-input mt-0 flex-shrink-0"
                                  checked={checked}
                                  onChange={() => {}}
                                  disabled={!isMaster}
                                  style={{ pointerEvents: 'none' }}
                                />
                                <div>
                                  <div className="fw-semibold" style={{ fontSize: 13 }}>
                                    {perm.name}
                                  </div>
                                  <div className="text-muted" style={{ fontSize: 11 }}>
                                    {perm.code}
                                  </div>
                                </div>
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        )} {/* !loading && !loadError 조건부 렌더 닫기 */}

      </div>

      {/* ── 역할 추가 모달 ── */}
      {modalOpen && (
        <div className="modal modal-blur fade show d-block" tabIndex="-1"
          style={{ background: 'rgba(0,0,0,0.4)' }}
          onMouseDown={onBackdropMouseDown} onClick={(e) => onBackdropClick(e, () => setModalOpen(false))}>
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content">
              <form onSubmit={handleCreate}>
                <div className="modal-header">
                  <h5 className="modal-title">역할 추가</h5>
                  <button type="button" className="btn-close" onClick={() => setModalOpen(false)} />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label required">역할 코드</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="예) OPERATOR"
                      value={newCode}
                      onChange={e => setNewCode(e.target.value.toUpperCase())}
                      required
                      pattern="[A-Z0-9_]+"
                      title="영문 대문자, 숫자, 언더스코어만 허용"
                    />
                    <div className="form-hint">영문 대문자, 숫자, _ 만 허용</div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label required">역할 이름</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="예) 운영 관리자"
                      value={newName}
                      onChange={e => setNewName(e.target.value)}
                      required
                    />
                  </div>
                  <div className="mb-0">
                    <label className="form-label">설명 (선택)</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="역할 설명"
                      value={newDesc}
                      onChange={e => setNewDesc(e.target.value)}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-sm btn-outline-secondary me-auto"
                    onClick={() => setModalOpen(false)}>
                    취소
                  </button>
                  <button type="submit" className="btn btn-sm btn-primary" disabled={creating}>
                    {creating
                      ? <span className="spinner-border spinner-border-sm me-1" />
                      : <IconPlus size={16} className="me-1" />}
                    생성
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
